import { useState, useCallback, useRef, useEffect } from "react";
import { Message, fetchConversation } from "@/services/messages";

const MESSAGES_PER_PAGE = 20;

interface UsePaginatedMessagesProps {
  userId: string;
  reagentId?: string;
}

interface UsePaginatedMessagesReturn {
  messages: Message[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  error: string | null;
  loadMessages: (pageNumber: number, append: boolean) => Promise<void>;
  loadMore: () => void;
  refreshConversation: () => void;
}

export function usePaginatedMessages({
  userId,
  reagentId
}: UsePaginatedMessagesProps): UsePaginatedMessagesReturn {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  
  const isMountedRef = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const loadMessages = useCallback(async (pageNumber: number, append: boolean) => {
    if (!userId) return;

    const isFirstPage = pageNumber === 0;
    
    if (isFirstPage) {
      setIsLoading(true);
    } else {
      setIsLoadingMore(true);
    }
    
    setError(null);

    try {
      const newMessages = await fetchConversation(
        userId,
        reagentId,
        MESSAGES_PER_PAGE,
        pageNumber
      );

      if (!isMountedRef.current) return;

      if (newMessages.length < MESSAGES_PER_PAGE) {
        setHasMore(false);
      }

      if (append && pageNumber > 0) {
        setMessages(prev => [...newMessages, ...prev]);
      } else {
        setMessages(newMessages);
        setHasMore(newMessages.length === MESSAGES_PER_PAGE);
      }
      
      setCurrentPage(pageNumber);
    } catch (err) {
      if (!isMountedRef.current) return;
      
      setError(err instanceof Error ? err.message : 'Failed to load messages');
      setMessages([]);
    } finally {
      if (isMountedRef.current) {
        if (isFirstPage) {
          setIsLoading(false);
        } else {
          setIsLoadingMore(false);
        }
      }
    }
  }, [userId, reagentId]);

  const loadMore = useCallback(() => {
    if (!isLoadingMore && hasMore) {
      loadMessages(currentPage + 1, true);
    }
  }, [currentPage, hasMore, isLoadingMore, loadMessages]);

  const refreshConversation = useCallback(() => {
    loadMessages(0, false);
  }, [loadMessages]);

  return {
    messages,
    isLoading,
    isLoadingMore,
    hasMore,
    error,
    loadMessages,
    loadMore,
    refreshConversation
  };
}
